import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "pending" });
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (stop) return;
        setState(data);
        if (data.payment_status !== "paid" && tries < 15) {
          setTimeout(() => setTries((t) => t + 1), 2000);
        }
      } catch (e) {
        setState({ status: "error", detail: e.message });
      }
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, tries]);

  const paid = state.payment_status === "paid";
  const failed = state.status === "error" || state.payment_status === "failed";

  return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
      {paid ? (
        <>
          <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-6" />
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight mb-3">Payment confirmed</h1>
          <p className="text-slate-600 mb-8">Your listing is now Premium — it will appear in featured sections across Domain Host Directory.</p>
          <Button asChild className="bg-blue-700 hover:bg-blue-800"><Link to="/dashboard" data-testid="success-return-dashboard">Back to dashboard</Link></Button>
        </>
      ) : failed ? (
        <>
          <XCircle className="h-16 w-16 text-red-600 mx-auto mb-6" />
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight mb-3">Something went wrong</h1>
          <p className="text-slate-600 mb-8">We couldn't confirm your payment. Please try again or contact support.</p>
          <Button asChild variant="outline"><Link to="/dashboard">Back to dashboard</Link></Button>
        </>
      ) : (
        <>
          <Loader2 className="h-14 w-14 text-blue-700 animate-spin mx-auto mb-6" />
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight mb-3">Confirming your payment…</h1>
          <p className="text-slate-500">This usually takes just a few seconds.</p>
        </>
      )}
    </div>
  );
}

export function PaymentCancelPage() {
  return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="payment-cancel-page">
      <XCircle className="h-16 w-16 text-slate-400 mx-auto mb-6" />
      <h1 className="text-3xl font-semibold text-slate-900 tracking-tight mb-3">Payment cancelled</h1>
      <p className="text-slate-600 mb-8">No charge was made. You can upgrade anytime from your dashboard.</p>
      <Button asChild className="bg-blue-700 hover:bg-blue-800"><Link to="/dashboard">Back to dashboard</Link></Button>
    </div>
  );
}
